import produce, { Draft } from "immer";
import { create } from "zustand";
import {
    loadTextFile,
    removeTextFile,
    saveTextFile,
} from "../utility/cache/manageTextFile";
import debounce from "../utility/debounce";
import useMainStore from "./mainStore";
import useDataStore from "./dataStore";
// import deleteTextFile from "../utility/backend/text_buckets/deleteTextFile";
import useAccountManagementStore from "./accountManagementStore";
import getTextFile from "../utility/backend/text_buckets/getTextFile";

type textFile = {
    id: string;
    content: string;
};

export type TextFilesStore = {
    textFiles: { [id: string]: textFile };
    getTextFile: (id: string) => Promise<textFile | undefined>;
    addTextFile: (id: string, content: string) => void;
    /**
     * ONLY Removes text file from textFilesStore.tsx -> textFiles.
     * DOES NOT remove the text file from the cloud or indexedDB
     * REMOVES text files using dataStore note.remove(id)
     */
    removeTextFile: (id: string) => void;
    clearTextFiles: (deleteFromCache?: boolean) => void;
    setTextFile: (id: string, content: string) => void;
    saveContent: (id: string, content: string, skipUpdateDate?: boolean) => Promise<boolean>;
    // deleteTextFile: (id: string) => void;
};

const useTextFilesStore = create<TextFilesStore>((set, get) => ({
    textFiles: {},

    /**
     * Retrieves a text file by ID, loading it from cache or cloud if necessary
     * @param id - The ID of the text file to retrieve
     * @returns Promise resolving to the text file or undefined if not found
     */
    getTextFile: async (id: string) => {
        // Return cached version if available
        if (get().textFiles[id]) {
            return get().textFiles[id];
        }

        try {
            // Try to load from local cache first
            let textFile = await loadTextFile(id);

            // If not in cache, try to fetch from cloud
            if (!textFile) {
                const accountStore = useAccountManagementStore.getState();

                // Check if user is logged in, if not and no text file, return new text file.
                if (
                    !accountStore?.userData?.id ||
                    accountStore?.userData?.id === "default"
                ) {
                    return { id, content: "" };
                }

                // Fetch from cloud
                textFile = await getTextFile(id);

                // If note doesn't exist in cloud or cache
                if (!textFile) {
                    console.log("Note doesn't exist in cloud or cache", id);
                    return { id, content: "" };
                }

                // Cache the retrieved file
                await saveTextFile(id, textFile);
            }

            // Update store with the retrieved file
            set(
                produce((draft: Draft<TextFilesStore>) => {
                    draft.textFiles[id] = {
                        id,
                        content: textFile,
                    };
                })
            );

            return { id, content: textFile };
        } catch (error) {
            console.error("Failed to retrieve note:", error);
            return undefined;
        }
    },

    addTextFile: (id: string, content: string) => {
        set((state) => ({
            textFiles: { ...state.textFiles, [id]: { id, content } },
        }));
    },
    removeTextFile: (id: string) => {
        if (get().textFiles[id] === undefined) return;

        set((state) => ({
            textFiles: Object.fromEntries(
                Object.entries(state.textFiles).filter(([key]) => key !== id)
            ),
        }));
    },
    clearTextFiles: (deleteFromCache?: boolean) => {
        const textFiles = Object.keys(useDataStore.getState().data.notes);

        if (deleteFromCache) {
            textFiles.forEach((textFileID) => {
                removeTextFile(textFileID);
            });
        }

        set({ textFiles: {} });
    },
    setTextFile: (id: string, content: string) => {
        set((state: TextFilesStore) => {
            return {
                textFiles: {
                    ...state.textFiles,
                    [id]: { id, content },
                },
            };
        });
    },
    saveContent: (id: string, content: string, skipUpdateDate?: boolean) => {
        const textData = get().textFiles[id]?.content || "";

        // If the content is the same, return true
        if (textData === content) return Promise.resolve(true);

        // Update the text file store (set store content)
        get().setTextFile(id, content);

        // Update folder last updated
        const folderID = useDataStore.getState().data.notes[id]?.folderID;
        if (folderID && !skipUpdateDate) {
            useDataStore.getState().set.folder(folderID, {
                updatedAt: new Date().toISOString(),
            });
        }

        if(!skipUpdateDate){
            useMainStore.getState().syncFunctions.addChangesMade("noteChanged");
        };

        // Save the text file (save to cache)
        return saveTextFile(id, content).then(() => {
            if(skipUpdateDate) return true;

            useDataStore.getState().set.note(id, {
                updatedAt: new Date().toString(),
                changesMade: false,
            });

            return true;
        });
    },
    // deleteTextFile: (id: string) => {
    //     useDataStore.getState().remove.note(id);
    //     removeTextFile(id);
    // },
}));

export default useTextFilesStore;
